import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useManagedAccountAccessSessionState } from '../../hooks/useManagedAccountAccessSessionState';
import { returnToManagementAccountSession } from '../../utils/managedAccountAccessSession';

const EXPIRATION_RETURN_BUFFER_MS = 30_000;

export const ManagedAccountAccessSessionBoundary = observer(
	function ManagedAccountAccessSessionBoundary(): React.ReactElement | null {
		const userSession = useUserSession();
		const { expiresUtc, isLoading, isManagedAccountAccess } = useManagedAccountAccessSessionState();
		const isReturningRef = React.useRef(false);

		React.useEffect(() => {
			if (isLoading || !isManagedAccountAccess || !expiresUtc) {
				return;
			}

			// `expiresUtc` is already a `number | null`, and the guard above excludes null,
			// so it is always a numeric epoch here — no string parsing needed.
			const expiresAt = expiresUtc;
			if (Number.isNaN(expiresAt)) {
				return;
			}

			const returnToParent = (): void => {
				if (isReturningRef.current) {
					return;
				}

				isReturningRef.current = true;
				toast.warning('Your managed account session is expiring. Returning you to the parent account.');
				returnToManagementAccountSession(userSession).catch(() => {
					toast.error('Unable to return to the parent account. Please log in again.');
				});
			};

			const delayMs = expiresAt - Date.now() - EXPIRATION_RETURN_BUFFER_MS;
			if (delayMs <= 0) {
				returnToParent();
				return;
			}

			const timeoutHandle = window.setTimeout(returnToParent, delayMs);
			return () => window.clearTimeout(timeoutHandle);
		}, [expiresUtc, isLoading, isManagedAccountAccess, userSession]);

		return null;
	}
);
